"use client";

import { useQuery } from "@tanstack/react-query";

// Types matching the component's existing state shapes
export interface CryptoPriceData {
  price: number;
  change24h: number;
}

export interface FearGreedData {
  value: number;
  classification: string;
  history: number[];
  historyChart?: { date: string; value: number; classification: string }[];
  historyChart7d?: { date: string; value: number; classification: string }[];
  historyChart90d?: { date: string; value: number; classification: string }[];
  historyChart180d?: { date: string; value: number; classification: string }[];
  historyChart365d?: { date: string; value: number; classification: string }[];
  yesterday?: { value: number; classification: string };
  lastWeek?: { value: number; classification: string };
  lastMonth?: { value: number; classification: string };
}

export interface GlobalCryptoData {
  totalMarketCapFormatted: string;
  marketCapChange24h: number;
  btcDominance: number;
}

export interface CryptoNewsItem {
  title: string;
  link: string;
  pubDate: string;
  image: string | null;
  crypto: "BTC" | "ETH" | "SOL" | "XRP";
  source?: "beincrypto" | "cointelegraph" | "dextools";
}

async function fetchCryptoPrices(): Promise<Record<string, CryptoPriceData>> {
  const prices: Record<string, CryptoPriceData> = {};

  // Binance: BTC, ETH, SOL, XRP, DOGE, BNB
  try {
    const symbols = ["BTCUSDT", "ETHUSDT", "SOLUSDT", "XRPUSDT", "DOGEUSDT", "BNBUSDT"];
    const response = await fetch(
      `https://api.binance.com/api/v3/ticker/24hr?symbols=${JSON.stringify(symbols)}`
    );
    if (response.ok) {
      const data = await response.json();
      data.forEach((item: { symbol: string; lastPrice: string; priceChangePercent: string }) => {
        const symbol = item.symbol.replace("USDT", "");
        prices[symbol] = {
          price: parseFloat(item.lastPrice),
          change24h: parseFloat(item.priceChangePercent),
        };
      });
    }
  } catch { /* Binance failed, continue */ }

  // Bybit: HYPE (not listed on Binance)
  try {
    const response = await fetch("https://api.bybit.com/v5/market/tickers?category=spot&symbol=HYPEUSDT");
    if (response.ok) {
      const data = await response.json();
      const ticker = data?.result?.list?.[0];
      if (ticker) {
        const lastPrice = parseFloat(ticker.lastPrice);
        const prevPrice = parseFloat(ticker.prevPrice24h);
        prices["HYPE"] = {
          price: lastPrice,
          change24h: prevPrice > 0 ? ((lastPrice - prevPrice) / prevPrice) * 100 : 0,
        };
      }
    }
  } catch { /* Bybit failed, continue */ }

  return prices;
}

async function fetchFearGreed(): Promise<FearGreedData | null> {
  const response = await fetch("/api/crypto/fear-greed");
  if (!response.ok) return null;
  const data = await response.json();
  return {
    value: data.value,
    classification: data.classification,
    history: data.history || [],
    historyChart: data.historyChart || [],
    historyChart7d: data.historyChart7d || [],
    historyChart90d: data.historyChart90d || [],
    historyChart180d: data.historyChart180d || [],
    historyChart365d: data.historyChart365d || [],
    yesterday: data.yesterday,
    lastWeek: data.lastWeek,
    lastMonth: data.lastMonth,
  };
}

async function fetchGlobalCrypto(): Promise<GlobalCryptoData | null> {
  const response = await fetch("/api/crypto/global");
  if (!response.ok) return null;
  const data = await response.json();
  return {
    totalMarketCapFormatted: data.totalMarketCapFormatted,
    marketCapChange24h: data.marketCapChange24h,
    btcDominance: data.btcDominance,
  };
}

async function fetchCryptoNews(): Promise<CryptoNewsItem[]> {
  const response = await fetch("/api/crypto-news");
  if (!response.ok) return [];
  const data = await response.json();
  return data.news || [];
}

export function useCryptoPrices(enabled: boolean) {
  return useQuery({
    queryKey: ["crypto", "prices"],
    queryFn: fetchCryptoPrices,
    staleTime: 10 * 1000, // 10s
    gcTime: 5 * 60 * 1000,
    refetchInterval: enabled ? 10 * 1000 : false,
    refetchIntervalInBackground: false, // Pause polling when tab is hidden
    enabled,
  });
}

export function useFearGreed(enabled: boolean) {
  return useQuery({
    queryKey: ["crypto", "fearGreed"],
    queryFn: fetchFearGreed,
    staleTime: 5 * 60 * 1000, // 5 min
    gcTime: 30 * 60 * 1000,
    refetchInterval: enabled ? 5 * 60 * 1000 : false,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function useGlobalCrypto(enabled: boolean) {
  return useQuery({
    queryKey: ["crypto", "global"],
    queryFn: fetchGlobalCrypto,
    staleTime: 60 * 1000, // 1 min
    gcTime: 10 * 60 * 1000,
    refetchInterval: enabled ? 60 * 1000 : false,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function useCryptoNews(enabled: boolean) {
  return useQuery({
    queryKey: ["crypto", "news"],
    queryFn: fetchCryptoNews,
    staleTime: 30 * 1000, // 30s
    gcTime: 10 * 60 * 1000,
    refetchInterval: enabled ? 30 * 1000 : false,
    refetchIntervalInBackground: false,
    enabled,
  });
}
